// SPDX-License-Identifier: AGPL-3.0-or-later
import { parsePhoneNumberFromString } from 'libphonenumber-js';
/**
 * Parse + validate a free-text phone number. Two-stage:
 *
 *   1. `parsePhoneNumberFromString(value, defaultCountry)` — handles
 *      international-format input (`+44 20 7946 0958`) without a
 *      country hint, AND domestic input (`(415) 555-1212`) when the
 *      country hint is correct.
 *   2. `.isValid()` — distinguishes "parses but not a real number"
 *      (e.g. `+1 000 000 0000`) from "parses and matches the country
 *      plan". `parsePhoneNumberFromString` alone returns truthy for
 *      both, so we need this second check.
 *
 * Defaults `defaultCountry` to 'US' when unspecified — that matches
 * the org-settings fallback in `apps/api`. Callers should still pass
 * the org default explicitly so the renderer's country picker drives
 * the parse.
 */
export function parseAndValidatePhone(value, defaultCountry = 'US') {
    const trimmed = value.trim();
    if (trimmed === '') {
        return { ok: false, reason: 'unparseable', message: 'Phone number is empty.' };
    }
    const country = normaliseCountry(defaultCountry);
    let parsed;
    try {
        parsed = parsePhoneNumberFromString(trimmed, country);
    }
    catch {
        parsed = undefined;
    }
    if (parsed === undefined) {
        return {
            ok: false,
            reason: 'unparseable',
            message: 'That doesn’t look like a phone number.',
        };
    }
    if (!parsed.isValid()) {
        return {
            ok: false,
            reason: 'invalid-number',
            message: parsed.country !== undefined
                ? `Not a valid ${parsed.country} phone number.`
                : 'Not a valid phone number.',
        };
    }
    const detectedCountry = parsed.country ?? country;
    return {
        ok: true,
        formatted: parsed.number,
        international: parsed.formatInternational(),
        country: detectedCountry,
        type: mapLineType(parsed.getType()),
    };
}
/**
 * Best-effort cast to libphonenumber-js's CountryCode type. Unknown
 * codes (e.g. a clinician typed 'XX' into the country picker) fall
 * back to 'US' so we never throw on parse.
 */
function normaliseCountry(input) {
    const upper = input.trim().toUpperCase();
    if (upper.length !== 2)
        return 'US';
    return upper;
}
function mapLineType(t) {
    // libphonenumber-js returns strings with FIXED_LINE-style underscores
    // for some line types. We normalise to lowercase + dash so the
    // downstream `type === 'mobile'` checks read naturally.
    if (t === undefined)
        return 'unknown';
    switch (t) {
        case 'MOBILE':
            return 'mobile';
        case 'FIXED_LINE':
            return 'fixed-line';
        case 'FIXED_LINE_OR_MOBILE':
            return 'fixed-line-or-mobile';
        case 'VOIP':
            return 'voip';
        case 'TOLL_FREE':
            return 'toll-free';
        case 'PREMIUM_RATE':
            return 'premium-rate';
        case 'SHARED_COST':
            return 'shared-cost';
        case 'PERSONAL_NUMBER':
            return 'personal-number';
        case 'PAGER':
            return 'pager';
        case 'UAN':
            return 'uan';
        default:
            return 'unknown';
    }
}
//# sourceMappingURL=phone.js.map